/**
 * SurfaceInsetGizmo — visual + interactive feedback for the Surface Inset
 * tool.
 *
 * Single post-pick stage `"edit"` exposes both gizmos at once:
 *   - **Inset ring** at the centroid — drag radially to grow the inset.
 *     Release just freezes the value; the operator stays in `"edit"`.
 *   - **Height arrow** along the basis normal — drag to extrude. Release
 *     confirms the operator and creates the boss / pocket. The arrow's
 *     anchor tracks the offset polygon (and the picked profile edge for
 *     ramp extrusions), so it follows the live inset.
 *   - **Edge-target hits + cap-hit mesh** — click a side edge to switch to
 *     a linear ramp aligned with that edge; click the cap interior to
 *     switch back to a constant-prism profile.
 *
 * The user can grab whichever gizmo they like in any order — the ring to
 * inset first, the arrow to extrude immediately with the full face boundary
 * as the footprint, or alternate.
 */
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import {
  selectSurfaceInsetDraft,
  selectSurfaceInsetStage,
  selectSurfaceToolOfKind,
} from "../../app/surfaceToolSelectors.ts";
import { surfaceInsetOffsetLoop } from "../../modeling/generators/surface.ts";
import { SURFACE_PREVIEW_HEIGHT_EPSILON } from "../../modeling/surfaceConstants.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import {
  DEFAULT_OPACITY,
  GIZMO_SCREEN_PX,
  OP_HANDLE_COLOR,
  OP_HANDLE_COLOR_HOVER,
  SURFACE_CUT_PREVIEW_COLOR,
  SURFACE_CUT_PREVIEW_COLOR_HOVER,
} from "./constants.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";
import { SurfaceExtrudeArrow } from "./SurfaceExtrudeArrow.tsx";
import { SurfaceInsetRing } from "./SurfaceInsetRing.tsx";
import {
  consumeSurfaceTargetPointer,
  SurfaceLoopEdgeTargetHits,
} from "./SurfaceProfileTargetHits.tsx";
import {
  applyLinePositions,
  buildSurfacePolyProfilePreview,
} from "./surfaceProfilePreview.ts";
import { useSurfaceToolGizmoCallbacks } from "./useSurfaceToolGizmoCallbacks.ts";

// Geometry is +Y-aligned — the "from" vector for setFromUnitVectors so
// rotating it onto the basis normal aligns the arrow shaft with the height
// axis (matches `SurfaceFootprintGizmo`'s arrow orientation).
const _upY = new THREE.Vector3(0, 1, 0);
const _scratchPos = new THREE.Vector3();
const _scratchNormal = new THREE.Vector3();

const _capHitInvisibleMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
  depthTest: false,
  depthWrite: false,
});

/**
 * Triangulate an offset loop in basis-UV via earcut and emit world-space
 * triangles at the base plane. Returns an empty array on degenerate input
 * (n<3 or zero-area). Used for the cap-face hit mesh.
 */
function buildCapHitTriangles(
  loopUv: ReadonlyArray<readonly [number, number]>,
  centroidWorld: readonly [number, number, number],
  tangent: readonly [number, number, number],
  bitangent: readonly [number, number, number],
): Float32Array {
  if (loopUv.length < 3) return new Float32Array(0);
  const outline = loopUv.map(([u, v]) => new THREE.Vector2(u, v));
  const triangles = THREE.ShapeUtils.triangulateShape(outline, []);
  if (triangles.length === 0) return new Float32Array(0);
  const out = new Float32Array(triangles.length * 3 * 3);
  for (let t = 0; t < triangles.length; t += 1) {
    const tri = triangles[t]!;
    for (let k = 0; k < 3; k += 1) {
      const i = tri[k]!;
      const u = loopUv[i]![0];
      const v = loopUv[i]![1];
      out[t * 9 + k * 3 + 0] =
        centroidWorld[0] + u * tangent[0] + v * bitangent[0];
      out[t * 9 + k * 3 + 1] =
        centroidWorld[1] + u * tangent[1] + v * bitangent[1];
      out[t * 9 + k * 3 + 2] =
        centroidWorld[2] + u * tangent[2] + v * bitangent[2];
    }
  }
  return out;
}

/**
 * Build a closed-loop line segment buffer in object-local UV coordinates,
 * extruded into 3D by basis projection. Buffer holds pairs of points: each
 * edge of the closed polygon as a separate line segment.
 */
function buildLoopWirePositions(
  loopUv: ReadonlyArray<readonly [number, number]>,
  centroidWorld: readonly [number, number, number],
  tangent: readonly [number, number, number],
  bitangent: readonly [number, number, number],
  zOffsetAlongNormal: number,
  normal: readonly [number, number, number],
): Float32Array {
  const n = loopUv.length;
  if (n < 2) return new Float32Array(0);
  const out = new Float32Array(n * 2 * 3);
  for (let i = 0; i < n; i += 1) {
    const a = loopUv[i]!;
    const b = loopUv[(i + 1) % n]!;
    const ax =
      centroidWorld[0] +
      a[0] * tangent[0] +
      a[1] * bitangent[0] +
      zOffsetAlongNormal * normal[0];
    const ay =
      centroidWorld[1] +
      a[0] * tangent[1] +
      a[1] * bitangent[1] +
      zOffsetAlongNormal * normal[1];
    const az =
      centroidWorld[2] +
      a[0] * tangent[2] +
      a[1] * bitangent[2] +
      zOffsetAlongNormal * normal[2];
    const bx =
      centroidWorld[0] +
      b[0] * tangent[0] +
      b[1] * bitangent[0] +
      zOffsetAlongNormal * normal[0];
    const by =
      centroidWorld[1] +
      b[0] * tangent[1] +
      b[1] * bitangent[1] +
      zOffsetAlongNormal * normal[1];
    const bz =
      centroidWorld[2] +
      b[0] * tangent[2] +
      b[1] * bitangent[2] +
      zOffsetAlongNormal * normal[2];
    out[i * 6 + 0] = ax;
    out[i * 6 + 1] = ay;
    out[i * 6 + 2] = az;
    out[i * 6 + 3] = bx;
    out[i * 6 + 4] = by;
    out[i * 6 + 5] = bz;
  }
  return out;
}

export function SurfaceInsetGizmo() {
  const stage = useStore(selectSurfaceInsetStage);
  const draft = useStore(selectSurfaceInsetDraft);
  const { camera, size: viewportSize } = useThree();

  const groupRef = useRef<THREE.Group>(null);
  const ringGroupRef = useRef<THREE.Group>(null);
  const arrowGroupRef = useRef<THREE.Group>(null);
  const boundaryWireRef = useRef<THREE.LineSegments>(null);
  const offsetWireRef = useRef<THREE.LineSegments>(null);
  const prismWireRef = useRef<THREE.LineSegments>(null);
  const edgeHitGroupRef = useRef<THREE.Group>(null);
  const capHitMeshRef = useRef<THREE.Mesh>(null);

  const boundaryGeoRef = useRef(new THREE.BufferGeometry());
  const offsetGeoRef = useRef(new THREE.BufferGeometry());
  const prismGeoRef = useRef(new THREE.BufferGeometry());
  const capHitGeoRef = useRef(new THREE.BufferGeometry());

  // Edge-target hit volumes need a stable per-vertex array to mount against;
  // recompute when the offset loop changes (boundary or insetDistance shift).
  // Memoised so both the React tree (mounting child components per edge)
  // and the per-frame `useFrame` consume the same cached arrays —
  // `surfaceInsetOffsetLoop` runs at most once per draft mutation rather
  // than four times per frame as it did before consolidation.
  const offsetData = useMemo(() => {
    if (!draft) return { loop: [], world: [] as [number, number, number][] };
    const loop = surfaceInsetOffsetLoop(
      draft.boundaryLoop,
      draft.insetDistance,
    );
    const world: [number, number, number][] = [];
    for (const [u, v] of loop) {
      world.push([
        draft.centroid[0] +
          u * draft.basis.tangent[0] +
          v * draft.basis.bitangent[0],
        draft.centroid[1] +
          u * draft.basis.tangent[1] +
          v * draft.basis.bitangent[1],
        draft.centroid[2] +
          u * draft.basis.tangent[2] +
          v * draft.basis.bitangent[2],
      ]);
    }
    return { loop, world };
  }, [draft]);
  const offsetWorldVertices = offsetData.world;

  const visible = stage === "edit" && draft !== null;
  const previewColor =
    draft && draft.height < 0 ? SURFACE_CUT_PREVIEW_COLOR : OP_HANDLE_COLOR;
  const previewHoverColor =
    draft && draft.height < 0
      ? SURFACE_CUT_PREVIEW_COLOR_HOVER
      : OP_HANDLE_COLOR_HOVER;

  const computeNormal = useCallback((): [number, number, number] | null => {
    const d = selectSurfaceInsetDraft(useStore.getState());
    return d ? d.basis.normal : null;
  }, []);

  const computeBasis = useCallback(() => {
    const d = selectSurfaceInsetDraft(useStore.getState());
    return d ? d.basis : null;
  }, []);

  const computeArrowAnchor = useCallback(():
    | [number, number, number]
    | null => {
    const d = selectSurfaceInsetDraft(useStore.getState());
    if (!d) return null;
    const offsetLoop = surfaceInsetOffsetLoop(d.boundaryLoop, d.insetDistance);
    if (offsetLoop.length < 3) return d.centroid;
    const verts: [number, number, number][] = [];
    for (const [u, v] of offsetLoop) {
      verts.push([
        d.centroid[0] + u * d.basis.tangent[0] + v * d.basis.bitangent[0],
        d.centroid[1] + u * d.basis.tangent[1] + v * d.basis.bitangent[1],
        d.centroid[2] + u * d.basis.tangent[2] + v * d.basis.bitangent[2],
      ]);
    }
    return buildSurfacePolyProfilePreview(
      verts,
      d.basis.normal,
      d.height,
      d.profileTarget,
    ).anchor;
  }, []);

  // Shared per-kind callback factory — owns setHeight / setDragging /
  // confirm / setProfile{Face,Edge}. The unique-to-Inset `setInset`
  // (continuous inset-distance update from the ring drag) is a one-line
  // wrapper around `patchDraft` since no other gizmo writes that field.
  const callbacks = useSurfaceToolGizmoCallbacks("inset");
  const {
    patchDraft,
    setHeight,
    setDragging,
    confirm: confirmHeight,
    setProfileFace,
    setProfileEdge,
  } = callbacks;
  const setInset = useCallback(
    (distance: number) => patchDraft({ insetDistance: distance }),
    [patchDraft],
  );

  // Ring drag-release: no-op. Both gizmos are live during `"edit"`, so
  // releasing the ring just freezes the current insetDistance. The user
  // can grab the ring again to adjust further or drag the arrow to extrude
  // (releasing the arrow is what confirms the operator).
  const onRingRelease = useCallback(() => {}, []);

  // Computed offset loop + height for per-frame wire updates.
  useFrame(() => {
    if (!visible || !draft || !groupRef.current) return;
    // Pulled from the memo — same value the React tree uses for the
    // edge-hit volumes and the cap-hit triangulation. Avoids the four
    // duplicate `surfaceInsetOffsetLoop` calls per frame the previous
    // implementation made.
    const offsetLoop = offsetData.loop;

    // Boundary loop wire (dim) — at face plane (z = 0 along normal).
    const boundaryPositions = buildLoopWirePositions(
      draft.boundaryLoop,
      draft.centroid,
      draft.basis.tangent,
      draft.basis.bitangent,
      0,
      draft.basis.normal,
    );
    const bgeo = boundaryGeoRef.current;
    bgeo.setAttribute(
      "position",
      new THREE.BufferAttribute(boundaryPositions, 3),
    );
    bgeo.attributes.position.needsUpdate = true;
    if (boundaryWireRef.current) {
      boundaryWireRef.current.geometry = bgeo;
      boundaryWireRef.current.visible = true;
    }

    // Offset loop wire (bright) — at face plane (the prism's cap base).
    const offsetPositions = buildLoopWirePositions(
      offsetLoop,
      draft.centroid,
      draft.basis.tangent,
      draft.basis.bitangent,
      0,
      draft.basis.normal,
    );
    const ogeo = offsetGeoRef.current;
    ogeo.setAttribute(
      "position",
      new THREE.BufferAttribute(offsetPositions, 3),
    );
    ogeo.attributes.position.needsUpdate = true;
    if (offsetWireRef.current) {
      offsetWireRef.current.geometry = ogeo;
      offsetWireRef.current.visible = true;
    }

    // Both gizmos are live during `"edit"`. The user picks whichever to
    // drag — ring → inset distance, arrow → height (release confirms).
    {
      // Hand off to the shared profile-preview builder. It produces the cap
      // + side-edge wire positions (constant for prism, per-vertex height
      // ramp when the user has picked an edge) and the arrow anchor in a
      // single call — same code path Surface Poly's gizmo uses, so ramp
      // visuals stay consistent across operators.
      const profilePreview = buildSurfacePolyProfilePreview(
        offsetWorldVertices,
        draft.basis.normal,
        draft.height,
        draft.profileTarget,
      );
      applyLinePositions(prismGeoRef.current, profilePreview.wirePositions);
      if (prismWireRef.current) {
        prismWireRef.current.geometry = prismGeoRef.current;
        // Prism only renders once the user has actually pulled some height.
        prismWireRef.current.visible =
          Math.abs(draft.height) > SURFACE_PREVIEW_HEIGHT_EPSILON;
      }

      if (arrowGroupRef.current) {
        const a = arrowGroupRef.current;
        a.visible = true;
        _scratchPos.set(
          profilePreview.anchor[0],
          profilePreview.anchor[1],
          profilePreview.anchor[2],
        );
        _scratchNormal.set(
          draft.basis.normal[0],
          draft.basis.normal[1],
          draft.basis.normal[2],
        );
        a.position.copy(_scratchPos);
        a.quaternion.setFromUnitVectors(_upY, _scratchNormal);
        a.scale.setScalar(
          screenToWorldRadius(
            GIZMO_SCREEN_PX,
            _scratchPos,
            camera,
            viewportSize.height,
          ),
        );
      }

      if (edgeHitGroupRef.current) {
        edgeHitGroupRef.current.visible = true;
      }

      // Cap-face hit mesh — triangulate the offset loop in basis-UV and
      // emit world-space triangles at the base plane. Catches clicks on the
      // polygon interior so the user can swap back from a ramped profile to
      // the constant-prism profile (the edge-cylinder hit volumes only
      // cover narrow strips along each edge). Same role as
      // `SurfaceFootprintGizmo`'s `footprintMeshRef` plane, generalized to
      // arbitrary polygons via earcut.
      if (capHitMeshRef.current) {
        const positions = buildCapHitTriangles(
          offsetLoop,
          draft.centroid,
          draft.basis.tangent,
          draft.basis.bitangent,
        );
        const geo = capHitGeoRef.current;
        geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geo.attributes.position.needsUpdate = true;
        capHitMeshRef.current.geometry = geo;
        capHitMeshRef.current.visible = positions.length > 0;
      }
    }

    if (ringGroupRef.current) {
      ringGroupRef.current.visible = true;
    }
  });

  // groupRef is stable across renders so the render-order stamp only
  // needs to run once on mount.
  useEffect(() => {
    const g = groupRef.current;
    if (g) applyGizmoRenderOrder(g, 999);
  }, []);

  const computeAnchorForRing = useMemo(() => {
    return () => {
      const tool = selectSurfaceToolOfKind(useStore.getState(), "inset");
      if (!tool?.draft || tool.stage !== "edit") return null;
      return tool.draft.centroid;
    };
  }, []);

  if (!visible) return null;

  return (
    <group ref={groupRef} userData={{ excludeFromExport: true }}>
      {/* Boundary loop (dim) — the picked face's outline. */}
      <lineSegments ref={boundaryWireRef} renderOrder={998}>
        <lineBasicMaterial
          color={OP_HANDLE_COLOR}
          transparent
          opacity={DEFAULT_OPACITY * 0.5}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Offset loop (bright preview) — the actual cut footprint. */}
      <lineSegments ref={offsetWireRef} renderOrder={999}>
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Prism wireframe — flat or ramped depending on the current profile
          target. Driven by `buildSurfacePolyProfilePreview`. Hidden when
          height is sub-epsilon. */}
      <lineSegments ref={prismWireRef} renderOrder={999}>
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      <group ref={ringGroupRef}>
        <SurfaceInsetRing
          computeAnchor={computeAnchorForRing}
          computeBasis={computeBasis}
          setInset={setInset}
          confirm={onRingRelease}
          setDragging={setDragging}
        />
      </group>

      <SurfaceExtrudeArrow
        ref={arrowGroupRef}
        computeAnchor={computeArrowAnchor}
        computeNormal={computeNormal}
        setHeight={setHeight}
        confirm={confirmHeight}
        setDragging={setDragging}
        color={previewColor}
        hoverColor={previewHoverColor}
      />

      {/* Cap-face hit mesh: triangulated invisible mesh on the offset loop's
          base plane that catches clicks on the polygon interior. Without
          this the user can switch to a ramp profile by clicking an edge
          but has no way back — the edge-cylinder hit volumes only cover
          narrow strips along each edge. Same role as `SurfaceFootprintGizmo`'s
          `footprintMeshRef` plane. */}
      {stage === "edit" && offsetWorldVertices.length >= 3 ? (
        <mesh
          ref={capHitMeshRef}
          material={_capHitInvisibleMaterial}
          renderOrder={998}
          userData={{ isGizmoHandle: true }}
          onPointerDown={(e) => {
            consumeSurfaceTargetPointer(e);
            setProfileFace();
          }}
        />
      ) : null}

      {/* Edge-target hits: clicking a side edge swaps the profile to a
          linear ramp aligned with that edge. Same affordance Surface Box /
          Cylinder / Poly expose. */}
      {stage === "edit" && offsetWorldVertices.length >= 3 ? (
        <group ref={edgeHitGroupRef}>
          <SurfaceLoopEdgeTargetHits
            vertices={offsetWorldVertices}
            onSelectFace={(e) => {
              consumeSurfaceTargetPointer(e);
              setProfileFace();
            }}
            onSelectEdge={(edgeIndex, e) => {
              consumeSurfaceTargetPointer(e);
              setProfileEdge(edgeIndex);
            }}
          />
        </group>
      ) : null}
    </group>
  );
}
